import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy, Plus } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import { useAccount } from '../contexts/AccountContext';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import UapiPayloadTree, { groupOperations } from './shared/UapiPayloadTree';
import { apiErrorMessage } from '../utils/apiError';
import { formatDateTime } from '../utils/datetime';

// Redactions — the Universal API's routes, and what each document they carry is stripped to.
//
// WHY A TAB OF ITS OWN. A set could only be seen by opening the payload that owns it, which answers
// "what does this document redact" and never "where is hotelid struck, and by whom" — the question
// asked the moment a consumer reports seeing a field it should not, or not seeing one it should.
//
// THE SAME TREE AS ADMIN > PAYLOADS, from the same component. It listed sets grouped by document in an
// order of its own, so the two screens showed the same API as two different lists and a reader could
// not tell whether they were looking at one thing from two angles or at two things.
//
// READ-ONLY, DELIBERATELY. A set is edited against its document's schema, on the payload's own
// redaction screen, because a rule is a JSON path into that schema and writing one anywhere else is
// writing it blind. This lists, counts and links; it does not duplicate that editor.
const UapiRedactionsTab = () => {
    // WHO A COPY BELONGS TO comes from the nav bar, not from a dialog. A property making its own version
    // of a system redaction is already working inside a chain and a hotel; asking again in a form is how
    // a set ends up owned by somebody who was not looking at it.
    const { selectedAccount, selectedHotel } = useAccount();
    const navigate = useNavigate();
    const [sets, setSets] = useState([]);
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [duplicating, setDuplicating] = useState(null);
    const [creating, setCreating] = useState(null);
    // Open by module, exactly as on the payloads screen — so everything starts shut and a module is
    // opened when somebody asks for it.
    const [expanded, setExpanded] = useState(() => new Set());
    const toggleModule = (module) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(module)) next.delete(module); else next.add(module);
        return next;
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // The tree and the sets together: the tree without its sets is the payloads screen, and the
            // sets without the tree are the list this screen used to be.
            const [opsRes, setsRes] = await Promise.all([
                legacyUapiService.listOperationMapping(),
                legacyUapiService.listAllRedactionSets(),
            ]);
            setOperations((opsRes.data || opsRes).operations || []);
            setSets((setsRes.data || setsRes).sets || []);
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to load redactions'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setsByPayload = useMemo(() => {
        const m = new Map();
        for (const s of sets) {
            const k = String(s.payload_id);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(s);
        }
        return m;
    }, [sets]);

    // MATCHED ON THE ROUTE, THE DOCUMENT AND THE SET TOGETHER. Somebody looking for "hotelid" does not
    // know which of the three carries the word they remember, and a search that only reads the path
    // would hide the set that mentions it.
    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return operations;
        return operations.filter(o => {
            if ([o.module_path, o.full_path, o.payload_name].some(v => String(v || '').toLowerCase().includes(q))) {
                return true;
            }
            return (setsByPayload.get(String(o.payload_id)) || [])
                .some(s => [s.name, s.description].some(v => String(v || '').toLowerCase().includes(q)));
        });
    }, [operations, search, setsByPayload]);

    // Only the documents that HAVE a body — a URL with no payload has nothing to strip, so this screen
    // has nothing to say about it and does not list it.
    const groups = useMemo(() => groupOperations(shown).filter(g => g.payloads.length), [shown]);
    const allExpanded = groups.length > 0 && groups.every(g => expanded.has(g.module));

    // WHO OWNS IT. A set with no owner is the platform's own — SYSTEM — and is read-only here: changing
    // it would change it for every property on the estate, which is not a decision that belongs on a
    // screen scoped to one chain.
    const ownerOf = (s) => (s.owner_hotel_id
        ? { kind: 'Hotel', name: s.owner_hotel_name, system: false }
        : s.owner_account_id
            ? { kind: 'Chain', name: s.owner_account_name, system: false }
            : { kind: 'System', name: null, system: true });

    // Duplicating a system set gives this chain (or this hotel) its own copy to depart from. The
    // original is untouched, and the copy applies to nobody until it is addressed to someone.
    const duplicate = async (s) => {
        setDuplicating(s.id);
        setError(null);
        try {
            // One name per document is a unique key, so the suffix grows until it is free rather than
            // failing on the second copy.
            const taken = new Set((setsByPayload.get(String(s.payload_id)) || [])
                .map(x => String(x.name).toLowerCase()));
            const label = selectedHotel ? 'hotel' : 'chain';
            let name = `${s.name} (${label})`;
            for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${s.name} (${label} ${n})`;
            await legacyUapiService.duplicateRedactionSet(s.id, {
                name,
                hotelId: selectedHotel || null,
                accountId: selectedHotel ? null : selectedAccount || null,
            });
            await load();
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not duplicate that redaction'));
        } finally {
            setDuplicating(null);
        }
    };

    // A REDACTION OF THIS CHAIN'S OWN, from nothing rather than from a copy. Duplicating is the usual
    // way in — a system set is a working starting point — but a document whose system set strikes
    // nothing has nothing worth copying, and starting from empty is then the shorter road.
    //
    // The rules are added on the editor, against the document's schema, which is where a JSON path can
    // be picked rather than typed. This makes the set and takes you there.
    const createOwnSet = async (pl) => {
        setCreating(pl.payload_id);
        setError(null);
        try {
            const taken = new Set((setsByPayload.get(String(pl.payload_id)) || [])
                .map(x => String(x.name).toLowerCase()));
            const base = selectedHotel ? 'Hotel redaction' : 'Chain redaction';
            let name = base;
            for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${base} ${n}`;
            const res = await legacyUapiService.createRedactionSet(pl.payload_id, {
                name,
                hotelId: selectedHotel || null,
                accountId: selectedHotel ? null : selectedAccount || null,
            });
            const created = (res.data || res).set;
            await load();
            // Straight to the editor: a set with no rules strikes nothing, so creating one and stopping
            // leaves a row that looks like protection and is not.
            if (created) {
                navigate(`/admin/universal-api/payloads/${pl.payload_id}/redactions`
                    + `?set=${created.id}&from=uapi-redactions`);
            }
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not create a redaction'));
        } finally {
            setCreating(null);
        }
    };

    // WHO USES IT, per grant kind rather than totalled: a set named on an access profile reaches every
    // property following that profile, one on a credential reaches a single caller, and an audience
    // scope reaches whoever it is addressed to. A single number would hide which.
    const usedBy = (s) => [
        [s.profile_grant_count, 'access profile call', 'access profile calls'],
        [s.credential_grant_count, 'credential grant', 'credential grants'],
        [s.binding_grant_count, 'binding grant', 'binding grants'],
        [s.scope_count, 'audience scope', 'audience scopes'],
    ].filter(([n]) => Number(n) > 0)
        .map(([n, one, many]) => `${n} ${Number(n) === 1 ? one : many}`)
        .join(', ');

    return (
        <>
            <div className="uapi-section-bar">
                <div>
                    <h2 className="uapi-section-title">Redactions</h2>
                    <p className="lu-flow-note" style={{ margin: '0.25rem 0 0' }}>
                        The API's routes, and what each document they carry is stripped to before it
                        leaves the gateway. The same tree as Admin, Universal API, Payloads — this is the
                        view of it a property works from.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                        className="form-input"
                        style={{ maxWidth: '16rem' }}
                        placeholder="Search routes or redactions…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Button
                        variant="secondary"
                        onClick={() => setExpanded(allExpanded ? new Set() : new Set(groups.map(g => g.module)))}
                        disabled={loading || !groups.length}
                    >
                        {allExpanded ? <ChevronRight className="icon" /> : <ChevronDown className="icon" />}
                        {allExpanded ? 'Collapse all' : 'Expand all'}
                    </Button>
                </div>
            </div>

            {error && <p className="ppw-error-msg">{error}</p>}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading redactions…</p></div>
            ) : groups.length === 0 ? (
                <div className="otx-table-wrap" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                        {operations.length ? `Nothing matches “${search}”.` : 'The Universal API publishes no documents yet.'}
                    </p>
                </div>
            ) : (
                /* showUrls is off: the URLs are the payloads screen's business, and here they are
                   noise between a document and the rules that strip it. */
                <UapiPayloadTree
                    groups={groups}
                    expanded={expanded}
                    onToggleModule={toggleModule}
                    showUrls={false}
                    renderPayloadExtra={(pl) => {
                        const list = setsByPayload.get(String(pl.payload_id)) || [];
                        if (!list.length) {
                            return (
                                <p className="uapi-redaction-empty">
                                    Nothing is stripped from this document — every caller granted it sees
                                    all of it.
                                </p>
                            );
                        }
                        return (
                            <table className="data-table uapi-redaction-table">
                                <thead>
                                    <tr>
                                        <th>Set</th>
                                        <th>Owner</th>
                                        <th>Rules</th>
                                        <th>Used by</th>
                                        <th>Updated</th>
                                        <th className="action-header" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map(s => {
                                        const owner = ownerOf(s);
                                        return (
                                        <tr key={s.id}>
                                            <td>
                                                <strong>{s.name}</strong>
                                                {/* The default is not one set among several: it is what
                                                    a caller gets when nothing else matches, so it is
                                                    marked rather than left to be recognised by name. */}
                                                {s.is_default === 'Y' && (
                                                    <span className="uapi-redaction-default">default</span>
                                                )}
                                                {s.description && (
                                                    <div className="lu-muted" style={{ fontSize: '0.78rem' }}>
                                                        {s.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {/* SYSTEM IS MARKED, not inferred from a blank. It is
                                                    the platform's own redaction, it is read-only here,
                                                    and a reader deciding whether they may change
                                                    something should not have to guess from an empty
                                                    cell. */}
                                                <span className={`uapi-redaction-owner${owner.system ? ' system' : ''}`}>
                                                    {owner.system ? 'System' : `${owner.kind}: ${owner.name || 'unnamed'}`}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(s.rule_count) > 0
                                                    ? s.rule_count
                                                    : <span className="lu-muted">strikes nothing</span>}
                                            </td>
                                            <td>{usedBy(s) || <span className="lu-muted">nothing yet</span>}</td>
                                            <td>{formatDateTime(s.updated_at)}</td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {/* Duplicating needs somewhere to put the copy. Without
                                                    a chain in the nav bar there is no owner to give it,
                                                    and the button says so rather than failing on click. */}
                                                <button
                                                    type="button"
                                                    className="ppp-detail-btn"
                                                    disabled={duplicating === s.id || !selectedAccount}
                                                    onClick={() => duplicate(s)}
                                                    title={selectedAccount
                                                        ? `Copy "${s.name}" for ${selectedHotel ? 'this hotel' : 'this chain'} to edit`
                                                        : 'Choose a chain in the navigation bar first — a copy belongs to one'}
                                                >
                                                    <Copy size={11} />
                                                    {duplicating === s.id ? 'Copying…' : 'Duplicate'}
                                                </button>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        );
                    }}
                    renderPayloadActions={(pl) => (
                        <span className="uapi-redaction-actions">
                            <button
                                type="button"
                                className="ppp-detail-btn ppp-detail-btn-primary"
                                disabled={creating === pl.payload_id || !selectedAccount}
                                onClick={() => createOwnSet(pl)}
                                title={selectedAccount
                                    ? `Add a redaction for ${selectedHotel ? 'this hotel' : 'this chain'} on this document`
                                    : 'Choose a chain in the navigation bar first — a redaction belongs to one'}
                            >
                                <Plus size={11} />
                                {creating === pl.payload_id ? 'Creating…' : 'New redaction'}
                            </button>
                            {/* `from` so the editor's Back returns HERE. It is the same editor Admin
                                uses, and without this it sends a property back to an admin screen they
                                were never on. */}
                            <Link
                                className="uapi-redaction-edit"
                                to={`/admin/universal-api/payloads/${pl.payload_id}/redactions?from=uapi-redactions`}
                            >
                                Edit this document’s redactions
                            </Link>
                        </span>
                    )}
                />
            )}
        </>
    );
};

export default UapiRedactionsTab;
